<template>
  <div v-if="isMobile" class="flex flex-col rounded-md border border-neutral-200">
    <UiLink :id="`cart-item-name-${item.id}`" :to="`/${item.slug}`" :underline="false">
      <UiImage
        v-if="item.image_url"
        :id="`cart-item-${item.id}`"
        :src="item.image_url"
        :alt="item.name"
        class="w-40 rounded-md"
      />
    </UiLink>

    <UiInputQuantity
      :id="`cart-item-quantity-${item.id}`"
      class="mr-3"
      :min="0"
      :start="currentQuantity"
      :disabled="isLoading"
      :pre-update-fn="(direction: 'dec' | 'inc', nextValue: number) => handleDeleteItem(direction, nextValue)"
      @update-quantity="updateQuantity"
    />
  </div>
  <div v-else class="mx-3 mb-4 flex flex-row">
    <div class="flex w-32 items-start">
      <UiLink :id="`cart-item-image-${item.id}`" :to="`/${item.slug}`" :underline="false">
        <UiImage
          v-if="item.image_url"
          :id="`cart-item-${item.id}`"
          :src="item.image_url"
          :alt="item.name"
          class="rounded-md"
        />
      </UiLink>
    </div>
    <div class="ml-4 flex w-full flex-col">
      <UiLink :id="`cart-item-name-${item.id}`" :to="`/${item.slug}`" :underline="false">
        <UiText class="text-black">{{ item.name }}</UiText>
      </UiLink>
      <UiLabel class="mt-1">
        {{ formatPrice(locale as Locale, item.total_price.amount, item.total_price.currency_code) }}
      </UiLabel>
      <div class="mt-3 flex flex-row items-center justify-between">
        <div class="flex flex-row items-center">
          <UiInputQuantity
            :id="`cart-item-quantity-${item.id}`"
            class="mr-3"
            :min="0"
            :start="currentQuantity"
            :disabled="isLoading"
            :pre-update-fn="(direction: 'dec' | 'inc', nextValue: number) => handleDeleteItem(direction, nextValue)"
            @update-quantity="updateQuantity"
          />
          <UiLoader v-if="isLoading" size="sm" />
        </div>
        <UiTooltip :id="`cart-item-remove-${item.id}`" :label="$t('removeModal.removeItem')">
          <UiIconButton
            :id="`cart-item-remove-${item.id}`"
            :icon="IconTrash"
            variant="secondary"
            :disabled="isLoading"
            @click="isModalOpen = !isModalOpen"
          />
        </UiTooltip>
        <UiModal
          :id="`cart-item-remove-${item.id}`"
          :is-open="isModalOpen"
          :toggle="(value: boolean) => (isModalOpen = value)"
          :close="() => (isModalOpen = false)"
          :title="$t('removeModal.removeItemText')"
          :confirm-label="$t('general.confirm')"
          :cancel-label="$t('general.cancel')"
          :confirm-click="removeItem"
          :cancel-click="() => (isModalOpen = false)"
        >
          <UiText type="p">{{ $t('removeModal.content') }}</UiText>
        </UiModal>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IconTrash } from '#components';

import type { CartItemProps } from '~/components/Cart/types';

import { useAlertStore } from '~/stores/alert/alertStore';
import { useAppStore } from '~/stores/app/appStore';
import { useCartStore } from '~/stores/cart/cartStore';
import { CartActionType } from '~/stores/cart/types';

const { isMobile } = useBreakpoints();
const { locale } = useI18n();
const alertStore = useAlertStore();

const cartStore = useCartStore();
const appStore = useAppStore();

const props = defineProps<CartItemProps>();

const isLoading = ref<boolean>(false);
const currentQuantity = ref<number>(props.item.quantity);
const isModalOpen = ref<boolean>(false);

const updateQuantity = (value: number): void => {
  currentQuantity.value = value;

  isLoading.value = true;

  cartStore
    .updateCart({
      actions: [
        {
          id: props.item.id,
          quantity: currentQuantity.value,
          type: CartActionType.CHANGE_QTY,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'cart.updateQuantitySuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = false));
};

const removeItem = (): void => {
  isLoading.value = true;

  cartStore
    .updateCart({
      actions: [
        {
          id: props.item.id,
          type: CartActionType.REMOVE_ITEM,
        },
      ],
      country: appStore.currentCountryCode,
      locale: locale.value as Locale,
    })
    .then(() =>
      alertStore.add({
        text: 'cart.removeItemSuccessMessage',
        title: 'general.successTitle',
        type: 'positive',
      }),
    )
    .finally(() => (isLoading.value = false));
};

const handleDeleteItem = (direction: 'dec' | 'inc', nextValue: number): boolean => {
  if (direction === 'dec' && nextValue === 0) {
    isModalOpen.value = true;
    return false;
  }

  return true;
};
</script>
